import React, { useState } from 'react';
import { 
  Calendar, 
  BookOpen, 
  Target, 
  Clock, 
  FileText, 
  ChevronDown, 
  ChevronRight,
  Download,
  Printer,
  GraduationCap,
  Laptop,
  CheckCircle2,
  AlertCircle,
  Users,
  Lightbulb,
  Activity,
  Link as LinkIcon,
  Code,
  Database,
  Globe,
  Shield,
  Network
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { year13ComputerStudiesPlanner } from '@/data/year13ComputerStudiesPlanner';

export default function YearlyPlannerPage() {
  const [activeStrand, setActiveStrand] = useState('strand1');
  const [expandedSession, setExpandedSession] = useState(null);
  const planner = year13ComputerStudiesPlanner;

  const getStrandIcon = (strandId) => {
    const icons = {
      'strand1': Code,
      'strand2': Database,
      'strand3': Globe,
      'strand4': Shield,
      'strand5': Network
    };
    return icons[strandId] || BookOpen;
  };

  const getStrandColor = (color) => {
    const colors = {
      'blue': 'bg-blue-500',
      'green': 'bg-green-500',
      'purple': 'bg-purple-500',
      'red': 'bg-red-500',
      'orange': 'bg-orange-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  const getStrandBorderColor = (color) => {
    const colors = {
      'blue': 'border-blue-500',
      'green': 'border-green-500',
      'purple': 'border-purple-500',
      'red': 'border-red-500',
      'orange': 'border-orange-500'
    };
    return colors[color] || 'border-gray-500';
  };

  const getStrandBgColor = (color) => {
    const colors = {
      'blue': 'bg-blue-50',
      'green': 'bg-green-50',
      'purple': 'bg-purple-50',
      'red': 'bg-red-50',
      'orange': 'bg-orange-50'
    };
    return colors[color] || 'bg-gray-50';
  };

  const getAssessmentTypeColor = (type) => {
    const typeColors = {
      'Practical': 'bg-blue-100 text-blue-800',
      'Theory': 'bg-purple-100 text-purple-800',
      'Project': 'bg-green-100 text-green-800',
      'Exam': 'bg-red-100 text-red-800',
      'Mixed': 'bg-orange-100 text-orange-800'
    };
    return typeColors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTermColor = (termIndex) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-orange-500', 'bg-purple-500'];
    return colors[termIndex] || 'bg-gray-500';
  };

  const toggleSession = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  // Find current strand
  const currentStrand = planner.strands.find(s => s.id === activeStrand);

  // Count total units across all strands
  const totalUnits = planner.strands.reduce((acc, strand) => acc + (strand.units?.length || 0), 0);

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Laptop className="w-8 h-8 text-purple-600" />
            Year 13 ICT
          </h1>
          <p className="text-gray-600 mt-1">Detailed Yearly Planner 2025 - {planner.school}</p>
          <p className="text-sm text-gray-500">Teacher: {planner.teacher}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Course Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Weeks</p>
                <p className="text-2xl font-bold">{planner.courseOverview.totalWeeks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Terms</p>
                <p className="text-2xl font-bold">{planner.courseOverview.termsPerYear}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Strands</p>
                <p className="text-2xl font-bold">{planner.strands.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Target className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Marks</p>
                <p className="text-2xl font-bold">{planner.totalMarks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Learning Outcomes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            Learning Outcomes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {planner.learningOutcomes.map((outcome, index) => (
              <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{outcome}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Strand Tabs */}
      <Tabs value={activeStrand} onValueChange={setActiveStrand} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          {planner.strands.map((strand) => {
            const IconComponent = getStrandIcon(strand.id);
            return (
              <TabsTrigger 
                key={strand.id} 
                value={strand.id}
                className={`flex items-center gap-2 data-[state=active]:${getStrandColor(strand.color).replace('bg-', 'bg-')}/20`}
              >
                <IconComponent className="w-4 h-4" />
                <span className="hidden sm:inline">{strand.name.split(':')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {planner.strands.map((strand) => {
          const IconComponent = getStrandIcon(strand.id);
          return (
            <TabsContent key={strand.id} value={strand.id} className="space-y-4">
              {/* Strand Header */}
              <Card className={`border-l-4 ${getStrandBorderColor(strand.color)}`}>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <IconComponent className={`w-6 h-6 text-${strand.color}-600`} />
                        {strand.name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {strand.description}
                      </CardDescription>
                    </div>
                    <Badge className={getStrandColor(strand.color)}>
                      {strand.units?.length || 0} Units
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Units with Detailed Lesson Plans */}
              {strand.units && strand.units.length > 0 ? (
                <Accordion type="single" collapsible className="space-y-2">
                  {strand.units.map((unit, unitIndex) => (
                    <AccordionItem key={unitIndex} value={`unit-${unit.id}`} className="border rounded-lg">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <div className="flex items-center gap-4 text-left flex-wrap">
                          <Badge variant="outline" className="font-mono">
                            {unit.unit}
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold">{unit.title}</p>
                            <p className="text-sm text-gray-500">{unit.weeks} • {unit.duration}</p>
                          </div>
                          <div className="flex gap-2">
                            {unit.linkedToScheme && (
                              <Badge variant="secondary" className="text-xs">
                                <LinkIcon className="w-3 h-3 mr-1" />
                                Linked to Scheme
                              </Badge>
                            )}
                            {unit.linkedToBlueprint && (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                Has Blueprint
                              </Badge>
                            )}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        {/* Detailed Lesson Plans */}
                        {unit.sessions && unit.sessions.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold flex items-center gap-2 text-lg">
                              <FileText className="w-5 h-5" />
                              Detailed Lesson Plans ({unit.sessions.length} sessions)
                            </h4>
                            
                            {unit.sessions.map((session) => (
                              <Card 
                                key={session.id} 
                                className={`border ${expandedSession === session.id ? `${getStrandBorderColor(strand.color)} shadow-md` : ''}`}
                              >
                                <CardHeader 
                                  className="cursor-pointer hover:bg-gray-50"
                                  onClick={() => toggleSession(session.id)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-full ${getStrandBgColor(strand.color)} flex items-center justify-center font-bold`}>
                                        {session.number}
                                      </div>
                                      <div>
                                        <CardTitle className="text-base">{session.title}</CardTitle>
                                        <CardDescription>
                                          {session.duration}
                                        </CardDescription>
                                      </div>
                                    </div>
                                    {expandedSession === session.id ? (
                                      <ChevronDown className="w-5 h-5 text-gray-400" />
                                    ) : (
                                      <ChevronRight className="w-5 h-5 text-gray-400" />
                                    )}
                                  </div>
                                </CardHeader>
                                
                                {expandedSession === session.id && (
                                  <CardContent className="pt-0">
                                    <Separator className="mb-4" />
                                    
                                    {/* Session Structure */}
                                    {session.structure && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                          <Clock className="w-4 h-4" />
                                          Session Structure
                                        </h5>
                                        <div className="flex flex-wrap gap-2">
                                          {session.structure.map((phase, i) => (
                                            <Badge key={i} variant="outline" className="text-xs">
                                              {phase.phase} ({phase.time})
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Introduction */}
                                    {session.introduction && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                          <Lightbulb className="w-4 h-4 text-yellow-500" />
                                          Introduction
                                        </h5>
                                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                          {session.introduction.map((item, i) => (
                                            <li key={i}>{item}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Main Activities */}
                                    {session.mainActivities && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                          <Activity className={`w-4 h-4 text-${strand.color}-500`} />
                                          Main Activities
                                        </h5>
                                        {session.mainActivities.map((activity, i) => (
                                          <div key={i} className={`mb-3 p-3 ${getStrandBgColor(strand.color)} rounded-lg`}>
                                            <p className="font-medium mb-2">{activity.name}</p>
                                            <ul className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                                              {activity.steps.map((step, j) => (
                                                <li key={j}>{step}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Materials */}
                                      {session.materials && (
                                        <div>
                                          <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                            <BookOpen className="w-4 h-4" />
                                            Materials
                                          </h5>
                                          <ul className="text-sm text-gray-600 space-y-1">
                                            {session.materials.map((item, i) => (
                                              <li key={i} className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                                                {item}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}

                                      {/* Assessment */}
                                      {session.assessment && (
                                        <div>
                                          <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                            <Target className="w-4 h-4 text-red-500" />
                                            Assessment
                                          </h5>
                                          <ul className="text-sm text-gray-600 space-y-1">
                                            {session.assessment.map((item, i) => (
                                              <li key={i} className="flex items-center gap-2">
                                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                                {item}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>

                                    {/* Homework */}
                                    {session.homework && session.homework.length > 0 && (
                                      <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                                        <h5 className="font-semibold text-sm mb-2 flex items-center gap-2 text-orange-800">
                                          <FileText className="w-4 h-4" />
                                          Homework
                                        </h5>
                                        <ul className="text-sm text-orange-700 space-y-1">
                                          {session.homework.map((item, i) => (
                                            <li key={i}>• {item}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Teacher Notes */}
                                    {session.teacherNotes && (
                                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                                        <h5 className="font-semibold text-sm mb-2 flex items-center gap-2 text-yellow-800">
                                          <Lightbulb className="w-4 h-4" />
                                          Teacher Notes
                                        </h5>
                                        <ul className="text-sm text-yellow-700 space-y-1">
                                          {session.teacherNotes.map((note, i) => (
                                            <li key={i}>• {note}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Differentiation */}
                                    {session.differentiation && (
                                      <div className="mt-4 p-3 bg-green-50 rounded-lg">
                                        <h5 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-800">
                                          <Users className="w-4 h-4" />
                                          Differentiation
                                        </h5>
                                        <div className="text-sm text-green-700 space-y-1">
                                          {session.differentiation.map((item, i) => (
                                            <p key={i} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                )}
                              </Card>
                            ))}
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <Card className="p-8 text-center text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No units available for this strand yet.</p>
                </Card>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Assessment Calendar */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Assessment Calendar 2025
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Term</TableHead>
                <TableHead>Week</TableHead>
                <TableHead>Assessment</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Marks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planner.assessmentCalendar.map((assessment, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Badge className={getTermColor(assessment.term - 1)}>
                      Term {assessment.term}
                    </Badge>
                  </TableCell>
                  <TableCell>Week {assessment.week}</TableCell>
                  <TableCell className="font-medium">{assessment.title}</TableCell>
                  <TableCell>
                    <Badge className={getAssessmentTypeColor(assessment.type)}>
                      {assessment.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-bold">{assessment.marks}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-gray-50 font-bold">
                <TableCell colSpan={4}>Total Marks</TableCell>
                <TableCell className="text-right text-purple-600">{planner.totalMarks}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
